import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  FileText,
  FolderKanban,
  NotebookText,
  Plus,
  Search,
  Settings2,
  Sparkles,
  Palette,
  ShieldCheck,
  Target,
  UserRound,
  Workflow,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { createAdminClient, getAdminClient, getAdminClients, updateAdminClient } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { BusinessCenterClientDetail, ClientRecord } from "../../lib/types";

type BrandValue = "kreatethedate" | "truevance_connect";

const brandOptions: Array<{ value: BrandValue; label: string; accent: string }> = [
  { value: "truevance_connect", label: "Truevance Connect", accent: "#06B6D4" },
  { value: "kreatethedate", label: "Kreatethedate", accent: "#FF6B35" },
];

const statusOptions = [
  "active",
  "lead",
  "onboarding",
  "in_progress",
  "on_hold",
  "completed",
  "archived",
];

const portalAccessOptions = ["preparation", "ready", "invite_sent", "active", "paused", "revoked"];

interface ClientCreateForm {
  brand: BrandValue;
  clientName: string;
  clientEmail: string;
  clientSlug: string;
  organizationName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  clientStatus: string;
  clientNotes: string;
  portalAccessStatus: string;
  portalAccessNotes: string;
  futureProjectNotes: string;
  cloudflareAccountOwnerEmail: string;
  cloudflareBillingUrl: string;
}

interface ClientDetailForm {
  clientName: string;
  clientEmail: string;
  organizationName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  clientStatus: string;
  clientNotes: string;
  portalAccessStatus: string;
  portalAccessNotes: string;
  futureProjectNotes: string;
  cloudflareAccountOwnerEmail: string;
  storageSetupStatus: string;
  cloudflareBillingUrl: string;
}

const blankCreateForm: ClientCreateForm = {
  brand: "truevance_connect",
  clientName: "",
  clientEmail: "",
  clientSlug: "",
  organizationName: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  clientStatus: "active",
  clientNotes: "",
  portalAccessStatus: "preparation",
  portalAccessNotes: "",
  futureProjectNotes: "",
  cloudflareAccountOwnerEmail: "",
  cloudflareBillingUrl: "",
};

export function BusinessCenterOSClientCenterPage() {
  const [selectedBrand, setSelectedBrand] = useState<BrandValue>("truevance_connect");
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [detail, setDetail] = useState<BusinessCenterClientDetail | null>(null);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [createForm, setCreateForm] = useState<ClientCreateForm>(blankCreateForm);
  const [editForm, setEditForm] = useState<ClientDetailForm | null>(null);

  useBusinessCenterOSBranding("Client Center", "Master client records, contact records, history, and brand-scoped portal preparation for Business Center OS.");

  useEffect(() => {
    let cancelled = false;
    setLoadingClients(true);
    setMessage("");
    getAdminClients({ brand: selectedBrand })
      .then((result) => {
        if (cancelled) return;
        setClients(result);
        setSelectedClientId((current) => (result.some((client) => client.id === current) ? current : result[0]?.id || ""));
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Unable to load client records.");
      })
      .finally(() => {
        if (!cancelled) setLoadingClients(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedClientId) {
      setDetail(null);
      setEditForm(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    setMessage("");
    getAdminClient(selectedClientId)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        setEditForm({
          clientName: result.client.clientName || "",
          clientEmail: result.client.clientEmail || "",
          organizationName: result.client.organizationName || "",
          contactName: result.client.contactName || "",
          contactEmail: result.client.contactEmail || "",
          contactPhone: result.client.contactPhone || "",
          clientStatus: result.client.clientStatus || "active",
          clientNotes: result.client.clientNotes || "",
          portalAccessStatus: result.client.portalAccessStatus || "preparation",
          portalAccessNotes: result.client.portalAccessNotes || "",
          futureProjectNotes: result.client.futureProjectNotes || "",
          cloudflareAccountOwnerEmail: result.client.cloudflareAccountOwnerEmail || "",
          storageSetupStatus: result.client.storageSetupStatus || "not_started",
          cloudflareBillingUrl: result.client.cloudflareBillingUrl || "",
        });
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Unable to load client detail.");
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClientId]);

  const filteredClients = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return clients.filter((client) => {
      if (!normalizedQuery) return true;
      return [
        client.clientName,
        client.organizationName,
        client.contactName,
        client.contactEmail,
        client.clientEmail,
        client.clientStatus,
        client.portalAccessStatus,
        client.clientNotes,
        client.futureProjectNotes,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
    });
  }, [clients, query]);

  const summary = useMemo(() => {
    const activeClients = clients.filter((client) => !["archived", "completed"].includes((client.clientStatus || "").toLowerCase())).length;
    const portalReady = clients.filter((client) => (client.portalAccessStatus || "").toLowerCase() === "active").length;
    const withHistory = clients.filter((client) => Number(client.proposalCount || 0) + Number(client.agreementCount || 0) + Number(client.paymentCount || 0) > 0).length;
    const withNotes = clients.filter((client) => Boolean(client.clientNotes?.trim())).length;
    return [
      { label: "Client records", value: clients.length, note: "Brand-scoped client profiles in the current view." },
      { label: "Active clients", value: activeClients, note: "Clients still in active delivery or onboarding." },
      { label: "Portal ready", value: portalReady, note: "Records prepared for future portal access." },
      { label: "With history", value: withHistory, note: "Profiles with proposal, agreement, or payment history." },
      { label: "With notes", value: withNotes, note: "Profiles with saved client notes." },
    ];
  }, [clients]);

  const selectedClient = clients.find((client) => client.id === selectedClientId);

  function updateCreateForm<K extends keyof ClientCreateForm>(key: K, value: ClientCreateForm[K]) {
    setCreateForm((current) => ({ ...current, [key]: value }));
  }

  function updateEditForm<K extends keyof ClientDetailForm>(key: K, value: ClientDetailForm[K]) {
    setEditForm((current) => (current ? { ...current, [key]: value } : current));
  }

  async function handleCreateClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreating(true);
    setMessage("");
    try {
      const targetBrand = createForm.brand;
      const primaryContactEmail = createForm.contactEmail.trim();
      const created = await createAdminClient({
        ...createForm,
        clientEmail: primaryContactEmail,
        contactEmail: primaryContactEmail,
        clientSlug: createForm.clientSlug || slugify(createForm.organizationName || createForm.clientName),
      });
      setMessage(`${created.client.clientName} was created inside the ${brandLabel(targetBrand)} workspace.`);
      setShowCreateForm(false);
      setSelectedBrand(targetBrand);
      setCreateForm({ ...blankCreateForm, brand: targetBrand });
      const refreshed = await getAdminClients({ brand: targetBrand });
      setClients(refreshed);
      setSelectedClientId(created.client.id);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create client.");
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedClient || !editForm) return;
    setSaving(true);
    setMessage("");
    try {
      await updateAdminClient(selectedClient.id, editForm);
      const [freshList, freshDetail] = await Promise.all([
        getAdminClients({ brand: selectedBrand }),
        getAdminClient(selectedClient.id),
      ]);
      setClients(freshList);
      setDetail(freshDetail);
      setEditForm({
        clientName: freshDetail.client.clientName || "",
        clientEmail: freshDetail.client.clientEmail || "",
        organizationName: freshDetail.client.organizationName || "",
        contactName: freshDetail.client.contactName || "",
        contactEmail: freshDetail.client.contactEmail || "",
        contactPhone: freshDetail.client.contactPhone || "",
        clientStatus: freshDetail.client.clientStatus || "active",
        clientNotes: freshDetail.client.clientNotes || "",
        portalAccessStatus: freshDetail.client.portalAccessStatus || "preparation",
        portalAccessNotes: freshDetail.client.portalAccessNotes || "",
        futureProjectNotes: freshDetail.client.futureProjectNotes || "",
        cloudflareAccountOwnerEmail: freshDetail.client.cloudflareAccountOwnerEmail || "",
        storageSetupStatus: freshDetail.client.storageSetupStatus || "not_started",
        cloudflareBillingUrl: freshDetail.client.cloudflareBillingUrl || "",
      });
      setMessage(`${freshDetail.client.clientName} was updated and the activity timeline was refreshed.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update client.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BusinessCenterOSAdminShell
      title="Client Center"
      description="Master client records for both brands, with brand-scoped profiles, contact records, proposal history, agreement history, payment history, notes, and portal preparation in one operational workspace."
      statusItems={summary.slice(0, 4).map((item) => ({ label: item.label, value: item.value }))}
    >
      <div className="grid gap-8 text-[#F8FAFC]">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {summary.map((item) => (
              <article key={item.label} className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">{item.label}</p>
                <p className="mt-3 font-display text-4xl font-bold text-[#1F2937]">{item.value}</p>
                <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">{item.note}</p>
              </article>
            ))}
          </div>

          <div className="grid gap-8 xl:grid-cols-[420px_minmax(0,1fr)]">
            <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
              <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Brand scope</p>
                    <h2 className="mt-1 font-display text-3xl font-bold">Client records</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm((current) => !current)}
                    className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {showCreateForm ? "Close create form" : "New client record"}
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {brandOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSelectedBrand(option.value);
                        setCreateForm((current) => ({ ...current, brand: option.value }));
                      }}
                      className={`business-center-os-pill focus-ring inline-flex min-h-10 items-center rounded-full px-4 text-sm font-bold transition ${
                        selectedBrand === option.value ? "business-center-os-pill--active" : ""
                      }`}
                      style={selectedBrand === option.value ? { borderColor: option.accent } : undefined}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <label className="relative block">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#06B6D4]" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 w-full rounded-2xl pl-11 pr-4 text-sm font-semibold"
                    placeholder="Search client name, organization, contact, notes, or status"
                  />
                </label>
              </div>

              {showCreateForm ? (
                <form onSubmit={handleCreateClient} className="mt-5 grid gap-4 rounded-3xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.04)] p-4 sm:p-5">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Brand" dark>
                      <select
                        value={createForm.brand}
                        onChange={(event) => updateCreateForm("brand", event.target.value as BrandValue)}
                        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                      >
                        {brandOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <TextInput
                      label="Client / Account name"
                      dark
                      value={createForm.clientName}
                      onChange={(value) => updateCreateForm("clientName", value)}
                    />
                    <TextInput
                      label="Organization name"
                      dark
                      value={createForm.organizationName}
                      onChange={(value) => updateCreateForm("organizationName", value)}
                    />
                    <TextInput
                      label="Client slug"
                      dark
                      value={createForm.clientSlug}
                      onChange={(value) => updateCreateForm("clientSlug", value)}
                    />
                    <TextInput
                      label="Primary contact name"
                      dark
                      value={createForm.contactName}
                      onChange={(value) => updateCreateForm("contactName", value)}
                    />
                    <TextInput
                      label="Primary contact email"
                      type="email"
                      dark
                      value={createForm.contactEmail}
                      onChange={(value) => updateCreateForm("contactEmail", value)}
                    />
                    <TextInput
                      label="Primary contact phone"
                      dark
                      value={createForm.contactPhone}
                      onChange={(value) => updateCreateForm("contactPhone", value)}
                    />
                    <Field label="Client status" dark>
                      <select
                        value={createForm.clientStatus}
                        onChange={(event) => updateCreateForm("clientStatus", event.target.value)}
                        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                      >
                        {statusOptions.map((status) => (
                          <option key={status} value={status}>
                            {labelize(status)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Portal access status" dark>
                      <select
                        value={createForm.portalAccessStatus}
                        onChange={(event) => updateCreateForm("portalAccessStatus", event.target.value)}
                        className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                      >
                        {portalAccessOptions.map((status) => (
                          <option key={status} value={status}>
                            {labelize(status)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <TextArea
                      label="Client notes"
                      dark
                      value={createForm.clientNotes}
                      onChange={(value) => updateCreateForm("clientNotes", value)}
                    />
                    <TextArea
                      label="Portal access prep notes"
                      dark
                      value={createForm.portalAccessNotes}
                      onChange={(value) => updateCreateForm("portalAccessNotes", value)}
                    />
                    <TextArea
                      label="Future project history notes"
                      dark
                      value={createForm.futureProjectNotes}
                      onChange={(value) => updateCreateForm("futureProjectNotes", value)}
                    />
                    <TextInput
                      label="Cloudflare owner email"
                      type="email"
                      dark
                      value={createForm.cloudflareAccountOwnerEmail}
                      onChange={(value) => updateCreateForm("cloudflareAccountOwnerEmail", value)}
                    />
                    <TextInput
                      label="Cloudflare billing URL"
                      dark
                      value={createForm.cloudflareBillingUrl}
                      onChange={(value) => updateCreateForm("cloudflareBillingUrl", value)}
                    />
                  </div>
                  <div className="flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setCreateForm({ ...blankCreateForm, brand: selectedBrand })}
                      className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center rounded-full px-4 text-sm font-extrabold"
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                    >
                      <Plus className="size-4" aria-hidden="true" />
                      {creating ? "Creating..." : "Create client"}
                    </button>
                  </div>
                </form>
              ) : null}

              <div className="mt-5 grid gap-4">
                {loadingClients ? (
                  <div className="business-center-os-loading rounded-3xl p-5">
                    <p className="text-sm font-semibold text-[#CBD5E1]">Loading client records for the selected brand.</p>
                    <div className="mt-4 grid gap-3">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={`client-skeleton-${index}`} className="rounded-2xl border border-[rgba(6,182,212,.12)] bg-[rgba(255,255,255,.04)] p-4">
                          <div className="h-3 w-28 rounded-full bg-[rgba(255,255,255,.18)]" />
                          <div className="mt-4 h-5 w-2/3 rounded-full bg-[rgba(255,255,255,.14)]" />
                          <div className="mt-3 h-3 w-full rounded-full bg-[rgba(255,255,255,.1)]" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : filteredClients.length ? (
                  filteredClients.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => setSelectedClientId(client.id)}
                      className={`business-center-os-card business-center-os-card--light text-left rounded-3xl p-4 transition hover:-translate-y-0.5 ${
                        selectedClientId === client.id ? "ring-2 ring-[#06B6D4]" : ""
                      }`}
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="grid gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-display text-2xl font-bold text-[#1F2937]">{client.clientName}</h3>
                            <Badge tone={client.brand === "truevance_connect" ? "teal" : "orange"}>{brandLabel(client.brand)}</Badge>
                            <Badge tone="slate">{labelize(client.clientStatus || "active")}</Badge>
                          </div>
                          <p className="text-sm font-semibold leading-6 text-[#475569]">{client.organizationName || "Organization not set"}</p>
                          <p className="text-sm font-semibold leading-6 text-[#64748B]">
                            {client.contactName || "Contact not set"}{client.contactEmail ? ` • ${client.contactEmail}` : ""}{client.contactPhone ? ` • ${client.contactPhone}` : ""}
                          </p>
                        </div>
                        <div className="grid gap-2 text-sm font-semibold text-[#334155] lg:text-right">
                          <p>Updated {formatDate(client.updatedAt)}</p>
                          <p>{client.proposalCount || 0} proposals • {client.agreementCount || 0} agreements • {client.paymentCount || 0} payments</p>
                        </div>
                      </div>
                      <p className="mt-3 text-sm font-semibold leading-6 text-[#475569]">
                        {client.clientNotes || "No notes saved yet. Open the client to add profile notes and portal prep details."}
                      </p>
                    </button>
                  ))
                ) : (
                  <div className="business-center-os-empty rounded-3xl p-6 text-center">
                    <p className="font-display text-2xl font-bold text-[#1F2937]">No client records for this brand yet</p>
                    <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                      Create the first brand-scoped client record to begin tracking notes, history, and portal preparation.
                    </p>
                  </div>
                )}
              </div>
            </section>

            <section className="grid gap-8">
              {message ? (
                <div className="business-center-os-card business-center-os-card--light rounded-3xl p-5">
                  <p className="text-sm font-semibold leading-7 text-[#1F2937]">{message}</p>
                </div>
              ) : null}

              {!selectedClient || loadingDetail || !detail || !editForm ? (
                <div className="business-center-os-empty rounded-3xl p-8">
                  <p className="font-display text-3xl font-bold text-[#1F2937]">
                    {loadingDetail ? "Loading client profile" : "Select a client record"}
                  </p>
                  <p className="mt-3 max-w-2xl text-sm font-semibold leading-7 text-[#475569]">
                    {loadingDetail
                      ? "The selected client profile, histories, and timeline are loading."
                      : "Choose a brand-scoped client on the left to review the profile, contact record, timeline, and proposal, agreement, and payment history."}
                  </p>
                </div>
              ) : (
                <>
                  <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                    <div className="flex flex-col gap-4 border-b border-[rgba(6,182,212,.16)] pb-5 lg:flex-row lg:items-end lg:justify-between">
                      <div className="grid gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#06B6D4]">Client profile</p>
                          <Badge tone="teal">{brandLabel(detail.client.brand)}</Badge>
                        </div>
                        <h2 className="font-display text-3xl font-bold">{detail.client.clientName}</h2>
                        <p className="text-sm font-semibold leading-7 text-[#CBD5E1]">
                          Master profile, contact record, notes, and portal readiness are edited here. Brand assignment is
                          fixed at creation time to preserve separation.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to="/admin/business-center-os"
                          className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold"
                        >
                          <ArrowLeft className="size-4" aria-hidden="true" />
                          Back to Business Center OS
                        </Link>
                      </div>
                    </div>

                    <form onSubmit={handleSaveClient} className="mt-5 grid gap-5">
                      <div className="grid gap-4 md:grid-cols-2">
                        <TextInput
                          label="Client / Account name"
                          dark
                          value={editForm.clientName}
                          onChange={(value) => updateEditForm("clientName", value)}
                        />
                        <TextInput
                          label="Client email"
                          type="email"
                          dark
                          value={editForm.clientEmail}
                          onChange={(value) => updateEditForm("clientEmail", value)}
                        />
                        <TextInput
                          label="Organization name"
                          dark
                          value={editForm.organizationName}
                          onChange={(value) => updateEditForm("organizationName", value)}
                        />
                        <Field label="Brand assignment" dark>
                          <div className="business-center-os-field business-center-os-field--dark flex min-h-12 items-center rounded-2xl px-4 text-sm font-bold">
                            {brandLabel(detail.client.brand)}
                          </div>
                        </Field>
                        <TextInput
                          label="Primary contact name"
                          dark
                          value={editForm.contactName}
                          onChange={(value) => updateEditForm("contactName", value)}
                        />
                        <TextInput
                          label="Primary contact email"
                          type="email"
                          dark
                          value={editForm.contactEmail}
                          onChange={(value) => updateEditForm("contactEmail", value)}
                        />
                        <TextInput
                          label="Primary contact phone"
                          dark
                          value={editForm.contactPhone}
                          onChange={(value) => updateEditForm("contactPhone", value)}
                        />
                        <Field label="Client status" dark>
                          <select
                            value={editForm.clientStatus}
                            onChange={(event) => updateEditForm("clientStatus", event.target.value)}
                            className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {labelize(status)}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Portal access status" dark>
                          <select
                            value={editForm.portalAccessStatus}
                            onChange={(event) => updateEditForm("portalAccessStatus", event.target.value)}
                            className="business-center-os-field business-center-os-field--dark focus-ring min-h-12 rounded-2xl px-4 text-sm font-bold"
                          >
                            {portalAccessOptions.map((status) => (
                              <option key={status} value={status}>
                                {labelize(status)}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <TextArea
                          label="Client notes"
                          dark
                          value={editForm.clientNotes}
                          onChange={(value) => updateEditForm("clientNotes", value)}
                        />
                        <TextArea
                          label="Portal access prep notes"
                          dark
                          value={editForm.portalAccessNotes}
                          onChange={(value) => updateEditForm("portalAccessNotes", value)}
                        />
                        <TextArea
                          label="Future project history notes"
                          dark
                          value={editForm.futureProjectNotes}
                          onChange={(value) => updateEditForm("futureProjectNotes", value)}
                        />
                        <TextInput
                          label="Cloudflare owner email"
                          type="email"
                          dark
                          value={editForm.cloudflareAccountOwnerEmail}
                          onChange={(value) => updateEditForm("cloudflareAccountOwnerEmail", value)}
                        />
                        <Field label="Storage setup status" dark>
                          <div className="business-center-os-field business-center-os-field--dark flex min-h-12 items-center rounded-2xl px-4 text-sm font-bold">
                            {labelize(editForm.storageSetupStatus || "not_started")}
                          </div>
                        </Field>
                        <TextInput
                          label="Cloudflare billing URL"
                          dark
                          value={editForm.cloudflareBillingUrl}
                          onChange={(value) => updateEditForm("cloudflareBillingUrl", value)}
                        />
                      </div>
                      <div className="flex flex-wrap justify-end gap-3">
                        <button
                          type="submit"
                          disabled={saving}
                          className="business-center-os-button business-center-os-button--primary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold disabled:opacity-60"
                        >
                          {saving ? "Saving..." : "Save client profile"}
                        </button>
                      </div>
                    </form>
                  </section>

                  <section className="grid gap-8 lg:grid-cols-2">
                    <div className="business-center-os-card business-center-os-card--light rounded-3xl p-5 sm:p-6">
                      <div className="border-b border-[rgba(31,41,55,.12)] pb-4">
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Contact record</p>
                        <h3 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Primary contact profile</h3>
                      </div>
                      <div className="mt-5 grid gap-4 md:grid-cols-2">
                        <InfoTile label="Name" value={detail.client.contactName || "Not set"} />
                        <InfoTile label="Email" value={detail.client.contactEmail || "Not set"} />
                        <InfoTile label="Phone" value={detail.client.contactPhone || "Not set"} />
                        <InfoTile label="Organization" value={detail.client.organizationName || "Not set"} />
                      </div>
                      <div className="mt-4 rounded-2xl border border-[rgba(20,184,166,.16)] bg-[rgba(20,184,166,.08)] p-4">
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#0F766E]">Portal preparation</p>
                        <p className="mt-2 text-sm font-semibold leading-7 text-[#1F2937]">
                          {detail.client.portalAccessStatus ? `${labelize(detail.client.portalAccessStatus)} • ` : ""}
                          {detail.client.portalAccessNotes || "No portal preparation notes yet."}
                        </p>
                      </div>
                    </div>

                    <div className="business-center-os-card business-center-os-card--light rounded-3xl p-5 sm:p-6">
                      <div className="border-b border-[rgba(31,41,55,.12)] pb-4">
                        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">Notes</p>
                        <h3 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">Client notes</h3>
                      </div>
                      <p className="mt-4 text-sm font-semibold leading-7 text-[#475569]">
                        Keep the working summary here. Every save updates the client timeline so the team can follow
                        what changed.
                      </p>
                      <div className="mt-4 rounded-2xl border border-[rgba(6,182,212,.14)] bg-[rgba(255,255,255,.92)] p-4">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-[#06B6D4]">Current summary</p>
                        <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-7 text-[#1F2937]">
                          {detail.client.clientNotes || "No client notes have been saved yet."}
                        </p>
                      </div>
                      <div className="mt-4 rounded-2xl border border-[rgba(6,182,212,.14)] bg-[rgba(255,255,255,.92)] p-4">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-[#06B6D4]">Future project prep</p>
                        <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-7 text-[#1F2937]">
                          {detail.client.futureProjectNotes || "No future project history notes have been captured yet."}
                        </p>
                      </div>
                    </div>
                  </section>

                  <section className="grid gap-8 lg:grid-cols-3">
                    <HistoryPanel
                      title="Proposal history"
                      eyebrow="Proposal records"
                      tone="light"
                      items={detail.proposalHistory}
                      emptyMessage="No proposals are linked to this client yet."
                      getLink={(item) => item.reviewUrl || "/admin/proposals"}
                      getMeta={(item) => `${labelize(item.status)} • ${formatDate(item.createdAt)}`}
                    />
                    <HistoryPanel
                      title="Agreement history"
                      eyebrow="Agreement records"
                      tone="light"
                      items={detail.agreementHistory}
                      emptyMessage="No agreements are linked to this client yet."
                      getLink={(item) => `/admin/agreement-records/${item.id}`}
                      getMeta={(item) => `${labelize(item.status)} • ${formatDate(item.createdAt)}`}
                    />
                    <HistoryPanel
                      title="Payment history"
                      eyebrow="Payment records"
                      tone="light"
                      items={detail.paymentHistory}
                      emptyMessage="No payments are linked to this client yet."
                      getLink={(item) => `/admin/payment-records/${item.id}`}
                      getMeta={(item) => `${labelize(item.displayStatus || item.status)} • ${formatDate(item.createdAt)}`}
                    />
                  </section>

                  <section className="business-center-os-card business-center-os-card--dark rounded-3xl p-5 sm:p-6">
                    <div className="border-b border-[rgba(6,182,212,.16)] pb-4">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">Timeline</p>
                      <h3 className="mt-1 font-display text-3xl font-bold">Activity timeline</h3>
                    </div>
                    <div className="mt-5 grid gap-3">
                      {detail.timeline.length ? (
                        detail.timeline.map((item) => (
                          <article
                            key={item.id}
                            className="rounded-2xl border border-[rgba(6,182,212,.16)] bg-[rgba(255,255,255,.05)] p-4"
                          >
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge tone="teal">{labelize(item.eventType)}</Badge>
                                  <h4 className="font-display text-xl font-bold text-[#F8FAFC]">{item.title}</h4>
                                </div>
                                <p className="mt-2 text-sm font-semibold leading-7 text-[#CBD5E1]">{item.description}</p>
                              </div>
                              <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
                                <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FDBA74]">{formatDate(item.createdAt)}</p>
                                {item.relatedUrl ? (
                                  <Link
                                    to={item.relatedUrl}
                                    className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-10 items-center gap-2 rounded-full px-3 text-xs font-extrabold"
                                  >
                                    Open record
                                    <ArrowRight className="size-3" aria-hidden="true" />
                                  </Link>
                                ) : null}
                              </div>
                            </div>
                          </article>
                        ))
                      ) : (
                        <div className="business-center-os-empty rounded-3xl p-6">
                          <p className="font-display text-2xl font-bold text-[#1F2937]">No timeline events yet</p>
                          <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">
                            Client saves and future history actions will appear here as the workspace is used.
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                </>
              )}
            </section>
          </div>
      </div>
    </BusinessCenterOSAdminShell>
  );
}

function HistoryPanel({
  title,
  eyebrow,
  tone,
  items,
  emptyMessage,
  getLink,
  getMeta,
}: {
  title: string;
  eyebrow: string;
  tone: "light" | "dark";
  items: Array<any>;
  emptyMessage: string;
  getLink: (item: any) => string;
  getMeta: (item: any) => string;
}) {
  return (
    <article className={`business-center-os-card business-center-os-card--${tone} rounded-3xl p-5 sm:p-6`}>
      <div className="border-b border-[rgba(31,41,55,.12)] pb-4">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#14B8A6]">{eyebrow}</p>
        <h3 className="mt-1 font-display text-3xl font-bold text-[#1F2937]">{title}</h3>
      </div>
      <div className="mt-5 grid gap-3">
        {items.length ? (
          items.slice(0, 5).map((item) => (
            <Link
              key={item.id}
              to={getLink(item)}
              className="rounded-2xl border border-[rgba(6,182,212,.14)] bg-[rgba(255,255,255,.94)] p-4 transition hover:-translate-y-0.5 hover:border-[rgba(6,182,212,.3)]"
            >
              <p className="font-display text-xl font-bold text-[#1F2937]">{item.proposalNumber || item.agreementNumber || item.invoiceNumber || item.id}</p>
              <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">{getMeta(item)}</p>
              <p className="mt-2 text-xs font-black uppercase tracking-[0.18em] text-[#06B6D4]">{item.displayStatus || labelize(item.status || "active")}</p>
            </Link>
          ))
        ) : (
          <div className="rounded-2xl border border-dashed border-[rgba(6,182,212,.18)] bg-[rgba(255,255,255,.9)] p-4">
            <p className="font-display text-xl font-bold text-[#1F2937]">No records yet</p>
            <p className="mt-2 text-sm font-semibold leading-7 text-[#475569]">{emptyMessage}</p>
          </div>
        )}
      </div>
    </article>
  );
}

function Field({
  label,
  children,
  dark = false,
}: {
  label: string;
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      {children}
    </label>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  dark = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        className={`business-center-os-field ${dark ? "business-center-os-field--dark" : ""} focus-ring min-h-12 rounded-2xl px-4 text-sm font-semibold`}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  dark = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  dark?: boolean;
}) {
  return (
    <label className={`grid gap-2 text-sm font-extrabold md:col-span-2 ${dark ? "text-[#F8FAFC]" : "text-[#1F2937]"}`}>
      {label}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className={`business-center-os-field ${dark ? "business-center-os-field--dark" : ""} focus-ring rounded-2xl px-4 py-3 text-sm font-semibold`}
      />
    </label>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(20,184,166,.16)] bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">{label}</p>
      <p className="mt-2 break-words text-sm font-bold leading-6 text-[#1F2937]">{value}</p>
    </div>
  );
}

function Badge({ children, tone }: { children: ReactNode; tone: "teal" | "orange" | "slate" }) {
  const tones = {
    teal: "border-[rgba(6,182,212,.18)] bg-[rgba(6,182,212,.1)] text-[#0F766E]",
    orange: "border-[rgba(255,107,53,.2)] bg-[rgba(255,107,53,.1)] text-[#C2410C]",
    slate: "border-[rgba(31,41,55,.12)] bg-[rgba(15,23,42,.06)] text-[#334155]",
  } as const;
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-black uppercase tracking-[0.16em] ${tones[tone]}`}>{children}</span>;
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    const fullTitle = `${title} | Business Center OS`;
    document.title = fullTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#1F2937");

    const faviconHref = buildBusinessCenterFavicon();
    const iconSelectors = ['link[rel="icon"]', 'link[rel="shortcut icon"]'] as const;
    const icons = iconSelectors
      .map((selector) => document.querySelector<HTMLLinkElement>(selector))
      .filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#FF6B35"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#1F2937"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <path d="M33 16h15c-4 3.3-6.8 7.6-6.8 13.6 0 6.2 3.2 10.6 7.8 15.4H33c-8.8 0-16-6.4-16-14.5S24.2 16 33 16Z" fill="#F8FAFC" opacity=".1"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function labelize(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function brandLabel(value?: string) {
  return value === "truevance_connect" ? "Truevance Connect" : "Kreatethedate";
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
